import { useQuery } from "@tanstack/react-query";
import { useAccessToken, useTenantData } from "@/hooks";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi } from "@/lib/utils";
import { mergePoliciesWithPendingSignatures } from "@/lib/utils/waiver-signatures-session";
import { WaiversPoliciesApi } from "@/openapi/generated";

/** Honor dashboard layout SSR prefetch; waiver accept still refetches explicitly. */
const POLICIES_BY_USER_STALE_TIME_MS = 5 * 60 * 1000;

interface UsePoliciesByUserIdGetProps {
  userId: number;
  page?: number;
  pageSize?: number;
  active?: boolean | null;
}

export const usePoliciesByUserIdGet = ({
  userId,
  page = 1,
  pageSize = 1000,
  active,
}: UsePoliciesByUserIdGetProps) => {
  const accessToken = useAccessToken();
  const { tenantId } = useTenantData();

  const {
    data,
    isLoading,
    isError,
    isSuccess,
    isPending,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: [QueryKeys.Waivers, "byUserId", userId, page, pageSize, active],
    queryFn: () => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }
      const service = createAuthenticatedApi(WaiversPoliciesApi, accessToken);
      return service
        .apiPoliciesUserIdGet({
          id: userId,
          page,
          pageSize,
          active: active ?? undefined,
          xTenantID: tenantId,
        })
        .then((data) =>
          mergePoliciesWithPendingSignatures(tenantId, userId, data),
        );
    },
    select: (data) =>
      mergePoliciesWithPendingSignatures(tenantId, userId, data),
    enabled: !!accessToken && !!userId && tenantId > 0,
    staleTime: POLICIES_BY_USER_STALE_TIME_MS,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  return {
    data,
    isLoading,
    isError,
    isSuccess,
    isPending,
    isFetching,
    refetch,
  };
};
